"use client";
import { useRouter, useParams } from 'next/navigation';
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react"; // שורה 3 המאוחדת
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AdminDashboard } from "./AdminDashboard";
import { useAuth } from "@/lib/auth-context";
import {
  BookOpen, Send, Loader2, Search, LogOut, Upload, UploadCloud, Filter, X,
  ChevronLeft, ChevronRight, ShieldCheck, MessageSquareText, ChevronDown, Play, Download,
  ExternalLink, Lightbulb, Trash2, Tag, Eye, Zap, Image as ImageIcon, Pencil, FileText, Calendar, Building2
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
// --- Types ---
type TabType = "training" | "tenders" | "admin" | "uploads";

interface TenderCard {
  id: number;
  tender_name: string;
  tender_number?: string;
  publisher?: string;
  summary?: string;
  category?: string;
  publication_date?: string;
  submission_deadline?: string;
  validity_period?: string;
  entitlements: string[];
  source_filename?: string;
  appendices?: {filename: string, file_path: string}[];
}

interface TenderDetail extends TenderCard {
  parsed_summary?: string;
  structured_data?: any;
  source_file_path?: string;
}

interface ManualCard {
  id: string;
  title: string;
  category: string;
  subject?: string;
  summary?: string;
  filename?: string;
  order_index?: number;
  appendices?: {filename: string, file_path: string}[];
}

interface ManualSequence {
  sequence: { id: string; title: string; is_current: boolean }[];
  next_step: { id: string; title: string } | null;
  prev_step: { id: string; title: string } | null;
}

interface ManualDetail {
  id: string;
  title: string;
  summary?: string;
  category?: string;
  tags?: string[];
  steps: {
    title: string;
    description: string;
    example?: string;
    visual_note?: string;
  }[];
  pdfUrl?: string;
  videoUrl?: string;
  filename?: string;
}
const groupManualsBySubject = (manualList: ManualCard[]) => {
  return manualList.reduce((acc: { [key: string]: ManualCard[] }, manual) => {
    const key = manual.subject || manual.category || "כללי";
    if (!acc[key]) acc[key] = [];
    acc[key].push(manual);
    return acc;
  }, {});
};
const groupManualsByCategory = (manualList: ManualCard[]) => {
    return manualList.reduce((acc: { [key: string]: ManualCard[] }, manual) => {
      const key = manual.category || "כללי/מנהלה";
      if (!acc[key]) acc[key] = [];
      acc[key].push(manual);
      return acc;
    }, {});
  };
export function AssistantHub({ initialManualId }: { initialManualId?: string }) {
  const { user, logout, authFetch } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>("training");
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastUserMsgRef = useRef<HTMLDivElement>(null);
  const [manualSequence, setManualSequence] = useState<ManualSequence | null>(null);
  const [page, setPage] = useState(0);
  const [totalManuals, setTotalManuals] = useState(0);
  const LIMIT = 20; // כמות המדריכים בכל עמוד
  // Chat States
  const [isChatMinimized, setIsChatMinimized] = useState(true);
  const [chatInput, setChatInput] = useState("");
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [messages, setMessages] = useState<{ role: "user" | "tuvia", content: string }[]>([
    { role: "tuvia", content: "שלום, כאן פקד טוביה. במה אפשר לעזור?" }
  ]);
  const [manuals, setManuals] = useState<ManualCard[]>([]); // הגדרת המשתנה
  const [selectedManual, setSelectedManual] = useState<ManualDetail | null>(null);
  // Content States
  const groupedManuals = useMemo(() => groupManualsBySubject(manuals), [manuals]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingManualData, setEditingManualData] = useState<any>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadSubCategory, setUploadSubCategory] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isManualLoading, setIsManualLoading] = useState(false);
  const [manualSearch, setManualSearch] = useState("");
  const [searchCategory, setSearchCategory] = useState("all");
  const [uploadType, setUploadType] = useState<"file" | "video">("file");
  const [allManuals, setAllManuals] = useState<{id: number, title: string}[]>([]);
  const [selectedManualForVideo, setSelectedManualForVideo] = useState("");
  const [videoUrlInput, setVideoUrlInput] = useState("");
  const [highlightedStep, setHighlightedStep] = useState<number | null>(null);
  const [uploadModule, setUploadModule] = useState("");
  const [uploadActionType, setUploadActionType] = useState<"new" | "update" | "appendix">("new");
  const [uploadTargetId, setUploadTargetId] = useState("");
  const [reanalyzeDocument, setReanalyzeDocument] = useState(false);
  const [isTender, setIsTender] = useState(false);
  // --- Tender States ---
  const [tenders, setTenders] = useState<TenderCard[]>([]);
  const [selectedTender, setSelectedTender] = useState<TenderDetail | null>(null);
  const [isTenderLoading, setIsTenderLoading] = useState(false);
  const [tenderFreeText, setTenderFreeText] = useState("");
  const [isAnalyzingTender, setIsAnalyzingTender] = useState(false);
  const [tenderSearch, setTenderSearch] = useState("");
  const API_URL = "https://api.tuviai.org";
  // --- לוגיקה לקיבוץ מדריכים ---
  const groupManualsByFile = (manualList: ManualCard[]) => {
    return manualList.reduce((acc: { [key: string]: ManualCard[] }, manual) => {
      const key = manual.filename || "אחר";
      if (!acc[key]) acc[key] = [];
      acc[key].push(manual);
      return acc;
    }, {});
  };

  const openFullEditModal = () => {
    if (selectedManual) {
      setEditingManualData(JSON.parse(JSON.stringify(selectedManual)));
      setIsEditModalOpen(true);
    }
  };

  const handleSaveFullEdit = async () => {
    if (!editingManualData) return;
    setIsManualLoading(true);

    try {
      const res = await authFetch(`https://api.tuviai.org/admin/update-full-manual/${editingManualData.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
           title: editingManualData.title,
           summary: editingManualData.summary,
           steps: editingManualData.steps
        }),
      });

      if (res.ok) {
         setIsEditModalOpen(false);
         fetchManualDetail(editingManualData.id);
         handleManualSearch();
         alert("המדריך המלא עודכן ונשמר בשרת!");
      } else {
         alert("שגיאה בשמירת המדריך מול השרת.");
      }
    } catch (e) {
       console.error(e);
       alert("תקלת תקשורת בעת השמירה.");
    } finally {
       setIsManualLoading(false);
    }
  };

  const handleUploadAction = async () => {
  // 1. בדיקת תקינות דינמית (לפי סוג הפעולה)
  const isNew = uploadActionType === "new";
  const hasRequiredField = isNew ? uploadSubCategory : uploadTargetId;

  if (!selectedFile || !uploadModule || !hasRequiredField) {
    alert("נא למלא את כל השדות (תחום, נושא/מסמך יעד), ולבחור קובץ.");
    return;
  }

  setIsUploading(true);

  // 2. בניית ה-FormData
  const formData = new FormData();
  formData.append("file", selectedFile);
  formData.append("category", uploadModule);
  formData.append("subject", uploadSubCategory || "עדכון_מערכת");
  formData.append("is_tender", String(isTender));
  formData.append("action_type", uploadActionType);

  if (uploadActionType !== "new" && uploadTargetId) {
      formData.append("target_id", uploadTargetId);
      formData.append("reanalyze", String(reanalyzeDocument));
  }

  const endpoint = isTender ? "https://api.tuviai.org/admin/upload_tender" : "https://api.tuviai.org/admin/upload";

  try {
    const response = await authFetch(endpoint, {
      method: "POST",
      body: formData,
    });

    if (response.ok) {
      alert("הפעולה הושלמה בהצלחה!");
      setUploadSubCategory("");
      setUploadModule("");
      setSelectedFile(null);
      if (isTender) {
          fetchTenders();
      } else {
          handleManualSearch();
      }
      // כאן אפשר להוסיף סגירה של המודל אם תרצה: setActiveTab("training");
    } else {
      // 3. טיפול חכם בשגיאות (כדי למנוע [object Object])
      const errData = await response.json().catch(() => ({}));

      let errorMessage = "שגיאת שרת לא ידועה";
      if (errData.detail) {
        // אם השרת מחזיר רשימת שגיאות (כמו ב-FastAPI validation)
        if (Array.isArray(errData.detail)) {
          errorMessage = errData.detail.map((e: any) => e.msg).join(", ");
        } else {
          errorMessage = typeof errData.detail === 'object'
            ? JSON.stringify(errData.detail)
            : errData.detail;
        }
      } else if (response.statusText) {
        errorMessage = response.statusText;
      }

      console.error("Server Error Details:", errData);
      alert(`שגיאה בהעלאה: ${errorMessage}`);
    }
  } catch (e) {
    console.error("Upload failed", e);
    alert("תקלת רשת בעת ההעלאה. אנא וודא שהשרת פעיל.");
  } finally {
    setIsUploading(false);
  }
};



  const handleClearChat = () => {
    setMessages([{ role: "tuvia", content: "שלום, כאן פקד טוביה. במה אפשר לעזור?" }]);
  };

  async function handleManualSearch(newPage = 0) {
    setIsManualLoading(true);
    try {
      const res = await authFetch("https://api.tuviai.org/search_manuals", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            query: manualSearch,
            category: searchCategory,
            page: newPage,    // הוספת העמוד
            limit: LIMIT      // הוספת המגבלה
        }),
      });
      const result = await res.json();

      // הנחה שה-API מחזיר { data: [...], total: X }
      setManuals(result.data || []);
      setTotalManuals(result.total || 0);
      setPage(newPage);
    } catch (e) {
      console.error("Search error:", e);
    } finally {
      setIsManualLoading(false);
    }
}



  const handleRenameSubject = async (oldSubject: string) => {
    const newSubject = prompt("שנה את שם הרובריקה עבור כל המדריכים בקבוצה זו:", oldSubject);
    if (!newSubject || newSubject.trim() === "" || newSubject === oldSubject) return;

    try {
      const res = await authFetch(`https://api.tuviai.org/admin/rename-subject`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          old_subject: oldSubject,
          new_subject: newSubject.trim()
        }),
      });

      if (res.ok) {
        handleManualSearch();
      } else {
        const errorData = await res.json();
        alert(`שגיאה: ${errorData.detail || "לא ניתן לעדכן את השם"}`);
      }
    } catch (e) {
      console.error("Rename Error:", e);
      alert("תקלה בתקשורת עם השרת");
    }
  };

  // --- Tender Functions ---
  const fetchTenders = async () => {
    setIsTenderLoading(true);
    try {
      const res = await authFetch(`${API_URL}/api/tenders`);
      if (res.ok) {
        const data = await res.json();
        setTenders(Array.isArray(data) ? data : []);
      } else {
        console.error("Tender fetch error: API returned", res.status);
        setTenders([]);
      }
    } catch (e) {
      console.error("Tender fetch error:", e);
      setTenders([]);
    } finally {
      setIsTenderLoading(false);
    }
  };

  const fetchTenderDetail = async (id: number) => {
    setIsTenderLoading(true);
    try {
      const res = await authFetch(`${API_URL}/api/tenders/${id}`);
      const data = await res.json();
      setSelectedTender(data);
    } catch (e) {
      console.error("Tender detail error:", e);
    } finally {
      setIsTenderLoading(false);
    }
  };

  const handleAnalyzeFreeText = async () => {
    if (!tenderFreeText.trim() || tenderFreeText.length < 20) {
      alert("הטקסט קצר מדי לניתוח מכרז.");
      return;
    }
    setIsAnalyzingTender(true);
    try {
      const res = await authFetch(`${API_URL}/api/parse-tender`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: tenderFreeText, source: "chat" }),
      });
      if (res.ok) {
        const data = await res.json();
        setTenderFreeText("");
        fetchTenders();
        if (data.tender_id) {
          fetchTenderDetail(data.tender_id);
        }
        alert("✅ ניתוח המכרז הושלם ונשמר!");
      } else {
        const err = await res.json();
        alert(`שגיאה: ${err.detail || "ניתוח נכשל"}`);
      }
    } catch (e) {
      console.error(e);
      alert("תקלת תקשורת בניתוח מכרז.");
    } finally {
      setIsAnalyzingTender(false);
    }
  };

  const handleDeleteTender = async (id: number, name: string) => {
    if (!window.confirm(`למחוק את המכרז "${name}"?`)) return;
    try {
      const res = await authFetch(`${API_URL}/api/tenders/${id}`, { method: "DELETE" });
      if (res.ok) {
        setSelectedTender(null);
        fetchTenders();
        alert("המכרז נמחק.");
      }
    } catch (e) {
      alert("מחיקת מכרז נכשלה.");
    }
  };

  const handleDownloadTender = async (tenderId: number, fallbackFilename: string) => {
    try {
      const response = await fetch(`${API_URL}/api/download-tender/${tenderId}`);
      if (!response.ok) throw new Error("הקובץ לא נמצא");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fallbackFilename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert("שגיאה בהורדת קובץ המכרז.");
    }
  };


  const handleRenameCategory = async (oldCategory: string) => {
    const newCategory = prompt("שנה את שם הנושא עבור כל המדריכים בקבוצה זו:", oldCategory);
    if (!newCategory || newCategory === oldCategory) return;

    try {
      const res = await authFetch(`https://api.tuviai.org/admin/rename-category`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ old_category: oldCategory, new_category: newCategory }),
      });
      if (res.ok) {
        handleManualSearch();
      }
    } catch (e) {
      alert("תקלה בעדכון שם הנושא");
    }
  };



  const fetchManualDetail = async (id: string) => {
    setIsManualLoading(true);
    try {
      const res = await authFetch(`https://api.tuviai.org/get_manual/${id}`);
      const data = await res.json();
      setSelectedManual({ ...data, id: id });

      const seqRes = await authFetch(`https://api.tuviai.org/get_manual_sequence/${id}`);
      const seqData = await seqRes.json();
      setManualSequence(seqData);

      setActiveTab("training");
      window.history.pushState(null, '', `/${id}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { console.error(e); }
    finally { setIsManualLoading(false); }
  };

  const handleDeleteVideo = async (id: string) => {
    if (!window.confirm("להסיר את הסרטון מהמדריך?")) return;
    try {
      const res = await authFetch(`https://api.tuviai.org/admin/delete_video/${id}`, { method: "POST" });
      if (res.ok) fetchManualDetail(id);
    } catch (e) { alert("מחיקת סרטון נכשלה"); }
  };

  const handleAddTag = async (id: string, currentTags: string[]) => {
    const newTag = prompt("הזן תגית חדשה (למשל: זיכוי, MIRO, רכב):");
    if (!newTag) return;
    const updatedTags = [...(currentTags || []), newTag];
    try {
      await authFetch(`https://api.tuviai.org/admin/update_tags/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tags: updatedTags }),
      });
      fetchManualDetail(id);
    } catch (e) { alert("עדכון תגיות נכשל"); }
  };

  const handleRemoveTag = async (manualId: string, tagToRemove: string) => {
    if (!window.confirm(`להסיר את התגית #${tagToRemove}?`)) return;
    const updatedTags = selectedManual?.tags?.filter(t => t !== tagToRemove) || [];
    try {
      const res = await authFetch(`https://api.tuviai.org/admin/update_tags/${manualId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tags: updatedTags }),
      });
      if (res.ok) setSelectedManual(prev => prev ? { ...prev, tags: updatedTags } : null);
    } catch (e) { alert("שגיאה בהסרת התגית"); }
  };

  const handleRenameManual = async (id: string, currentTitle: string) => {
    const newTitle = prompt("הזן שם חדש:", currentTitle);
    if (!newTitle || newTitle === currentTitle) return;
    try {
      const res = await authFetch(`https://api.tuviai.org/admin/rename-manual/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: newTitle }),
      });
      if (res.ok) {
        handleManualSearch();
        if (selectedManual) setSelectedManual({ ...selectedManual, title: newTitle });
      }
    } catch (e) { alert("תקלה בעדכון."); }
  };

  const handleReanalyze = async (id: string) => {
    if (!window.confirm("להריץ ניתוח AI מחדש? הפעולה עשויה לקחת דקה.")) return;
    setIsManualLoading(true);
    try {
      const res = await authFetch(`https://api.tuviai.org/admin/reanalyze/${id}`, { method: "POST" });
      if (res.ok) await fetchManualDetail(id);
    } catch (e) { alert("ניתוח נכשל."); }
    finally { setIsManualLoading(false); }
  };

  const handleEditManual = async (id: string, currentTitle: string, currentSummary: string = "") => {
    const newTitle = prompt("ערוך כותרת מדריך:", currentTitle);
    if (newTitle === null) return;
    const newSummary = prompt("ערוך תקציר מדריך:", currentSummary);
    if (newSummary === null) return;
    try {
      const res = await authFetch(`https://api.tuviai.org/admin/update-manual/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: newTitle, summary: newSummary }),
      });
      if (res.ok) {
        handleManualSearch();
        fetchManualDetail(id);
        alert("המדריך עודכן בהצלחה בשרת.");
      } else {
        alert("שגיאה בעדכון המדריך.");
      }
    } catch (e) { alert("תקלה בתקשורת עם השרת בעת העדכון."); }
  };

  const handleDeleteManual = async (id: string, title: string) => {
    if (!window.confirm(`להשמיד את המדריך "${title}" וכל רכיביו?`)) return;
    try {
      const res = await authFetch(`https://api.tuviai.org/admin/delete-manual/${id}`, { method: "DELETE" });
      if (res.ok) {
        setSelectedManual(null);
        handleManualSearch();
        alert("המדריך נמחק בהצלחה מהשרת.");
      } else {
        alert("שגיאה במחיקת המדריך מהשרת.");
      }
    } catch (e) { alert("מחיקה נכשלה בגלל שגיאת תקשורת."); }
  };

  const handleVideoUpdate = async () => {
    if (!selectedManualForVideo || !videoUrlInput) return;
    setIsUploading(true);
    try {
      const res = await authFetch(`https://api.tuviai.org/admin/update_video`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ manual_id: selectedManualForVideo, video_url: videoUrlInput }),
      });
      if (res.ok) {
        setVideoUrlInput("");
        setSelectedManualForVideo("");
        alert("הסרטון שויך בהצלחה!");
        handleManualSearch();
      }
    } catch (e) { alert("עדכון סרטון נכשל."); }
    finally { setIsUploading(false); }
  };

  const handleChatSend = async () => {
    if (!chatInput.trim()) return;
    const userMsg = chatInput;
    setChatInput("");

    const currentHistory = [...messages];
    setMessages(prev => [...prev, { role: "user", content: userMsg }, { role: "tuvia", content: "" }]);
    setIsChatLoading(true);
    setTimeout(() => {
    lastUserMsgRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, 100);

    try {
      const response = await authFetch("https://api.tuviai.org/ask", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: userMsg,
          history: currentHistory.slice(-5).map(msg => ({
            role: msg.role === "tuvia" ? "assistant" : "user",
            content: msg.content
          })),
          context: selectedManual ? {
            manual_id: selectedManual.id,
            title: selectedManual.title,
            current_steps: selectedManual.steps
          } : null
        }),
      });

      if (!response.ok) throw new Error(`שגיאה: ${response.status}`);

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let accumulatedContent = "";

      if (!reader) throw new Error("לא ניתן לאתחל קורא");

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        accumulatedContent += chunk;

        const displayContent = accumulatedContent.replace(/\[FOCUS:\d+\]/g, "");

        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1].content = displayContent;
          return updated;
        });
      }
    } catch (e: any) {
      console.error(e);
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1].content = `⚠️ **נתק בשידור.** \n(${e.message}). \n\nעבור.`;
        return updated;
      });
    } finally {
      setIsChatLoading(false);
    }
  };

  const handleDownload = async (manualId: string | number, fallbackFilename: string) => {
    try {
      const response = await fetch(`https://api.tuviai.org/download_manual/${manualId}`, {
        method: 'GET',
      });
      if (!response.ok) throw new Error('הקובץ לא נמצא בשרת');

      const contentDisposition = response.headers.get('Content-Disposition');
      let finalFilename = fallbackFilename;

      if (contentDisposition && contentDisposition.includes("filename*=UTF-8''")) {
        const parts = contentDisposition.split("filename*=UTF-8''");
        if (parts.length > 1) {
          finalFilename = decodeURIComponent(parts[1]);
        }
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = finalFilename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Download fail:", err);
      alert("שגיאה בהורדת הקובץ. וודא שהקובץ קיים במערכת.");
    }
  };

  const tabs = [
    { id: "training" as TabType, label: "מרכז שימור ידע", icon: BookOpen },
    { id: "tenders" as TabType, label: "מכרזים", icon: BookOpen }
  ];
// --- Effects ---
useEffect(() => {
  if (activeTab === "admin" || activeTab === "uploads") {
    setIsChatMinimized(true);
  }
}, [activeTab]);
  useEffect(() => {
    handleManualSearch();
  }, [searchCategory]);

  useEffect(() => {
    if (activeTab === "tenders") {
      fetchTenders();
    }
  }, [activeTab]);

  useEffect(() => {
    if (initialManualId && !selectedManual) {
      fetchManualDetail(initialManualId);
    }
  }, [initialManualId]);

  useEffect(() => {
    if (highlightedStep !== null) {
      const timer = setTimeout(() => setHighlightedStep(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [highlightedStep]);


  useEffect(() => {
    const socket = new WebSocket("wss://api.tuviai.org/ws/updates");
    socket.onopen = () => console.log("Connected to WS");
    return () => {
      if (socket.readyState === 1) {
        socket.close();
      }
    };
  }, []);

  useEffect(() => {
  if (activeTab === "uploads") {
    // טעינת מדריכים
    authFetch("https://api.tuviai.org/admin/manuals_list")
      .then(res => res.json())
      .then(data => setAllManuals(data));

    // טעינת מכרזים (הוספנו את זה כדי שהדרופדאון לא יהיה ריק!)
    authFetch("https://api.tuviai.org/api/tenders")
      .then(res => res.json())
      .then(data => setTenders(Array.isArray(data) ? data : []));
  }
}, [activeTab]);

  return (
    <div className="flex min-h-screen flex-col bg-[#F8FAFC] font-sans" dir="rtl">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-white px-4 md:px-6 py-4 shadow-sm">
        <div className="mx-auto flex max-w-6xl flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-3 md:gap-4 shrink-0">
            <div className="h-13 w-13 md:h-13 md:w-13 rounded-2xl flex items-center justify-center shadow-md overflow-hidden border border-white/10">
              <img src="/apple-icon.png" alt="טוביה לוגו" className="h-full w-full object-contain p-0" />
            </div>
            <div className="flex flex-col justify-center">
              <h1 className="text-base md:text-xl font-black text-slate-900 leading-none">תשאלו את טוביה</h1>
              <div className="flex flex-col">
                <p className="flex items-center gap-2 text-[9px] md:text-[10px] font-extrabold text-slate-500 uppercase mt-1.5 tracking-wider">
                  <span>עוזר לוגיסטי חכם</span>
                  <span className="bg-blue-50 text-blue-600 text-[8px] md:text-[9px] font-black px-2 py-0.5 rounded-md border border-blue-100 tracking-tighter inline-flex items-center shadow-sm">Beta v1.0</span>
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1 md:gap-2">
            {user?.role?.toLowerCase() === "admin" && (
              <div className="flex gap-1 md:gap-2">
                <Button variant="outline" onClick={() => setActiveTab("uploads")} className="rounded-full h-9 px-3 md:px-4 md:h-10 border-blue-200 text-blue-900 font-bold transition-all hover:bg-blue-50 text-[11px] md:text-sm">
                  <UploadCloud size={14} className="ml-1 md:ml-2"/>
                  <span className="hidden xs:inline">העלאות</span>
                </Button>
                <Button variant="outline" onClick={() => setActiveTab("admin")} className="rounded-full h-9 px-3 md:px-4 md:h-10 border-blue-200 text-blue-900 font-bold transition-all hover:bg-blue-50 text-[11px] md:text-sm">
                  <ShieldCheck size={14} className="ml-1 md:ml-2"/>
                  <span className="hidden xs:inline">ניהול</span>
                </Button>
              </div>
            )}
            <Button variant="ghost" onClick={logout} className="text-slate-400 hover:text-red-500 rounded-full h-9 w-9 md:h-10 md:w-10 transition-colors shrink-0">
              <LogOut size={18} />
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <nav className="mx-auto w-full max-w-6xl px-6 mt-6">
        <div className="flex gap-2 bg-slate-200/50 p-1 rounded-2xl w-fit shadow-inner">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setSelectedManual(null); setManualSequence(null); }}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-black transition-all ${
                activeTab === tab.id ? "bg-white text-blue-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <tab.icon size={18} /> {tab.label}
            </button>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <main className="mx-auto w-full max-w-6xl flex-1 px-6 py-10">
        {activeTab === "training" && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            {!selectedManual ? (
              <>
                <div className="text-center mb-12">
                  <h2 className="text-4xl font-black text-slate-900 mb-2 tracking-tight">מרכז שימור ידע</h2>
                  <div className="mx-auto max-w-5xl shadow-2xl rounded-[24px] md:rounded-[32px] border border-slate-100 bg-white overflow-hidden mt-8 flex flex-col md:flex-row">
                    <div className="w-full md:w-72 border-b md:border-b-0 md:border-l border-slate-100 flex items-center bg-slate-50/30">
                      <Select value={searchCategory} onValueChange={setSearchCategory}>
                        <SelectTrigger className="h-12 md:h-16 w-full border-none px-4 md:px-8 font-black text-blue-900 focus:ring-0 text-sm md:text-base">
                          <Filter size={18} className="ml-2 md:ml-3" />
                          <SelectValue placeholder="כל המדריכים" />
                        </SelectTrigger>
                        <SelectContent className="font-bold">
                          <SelectItem value="all">🔍 כל המדריכים</SelectItem>
                          <SelectItem value="inventory">📦 ניהול מלאי</SelectItem>
                          <SelectItem value="fleet">🚗 צי רכב</SelectItem>
                          <SelectItem value="procurement">🛒 רכש ואספקה</SelectItem>
                          <SelectItem value="general">📂 כללי / מנהלה</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Input
                      placeholder="חפש מדריך SAP, טרנזקציה או נושא..."
                      className="h-12 md:h-16 flex-1 border-none text-right font-medium pr-4 md:pr-8 focus:ring-0 text-sm md:text-base"
                      value={manualSearch}
                      onChange={(e) => setManualSearch(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleManualSearch()}
                    />
                    <Button
                      onClick={handleManualSearch}
                      disabled={isManualLoading}
                      className="h-12 md:h-16 px-8 md:px-12 bg-blue-900 text-white font-black text-lg md:text-xl hover:bg-blue-800 transition-all active:scale-95"
                    >
                      {isManualLoading ? <Loader2 className="animate-spin" /> : "חפש"}
                    </Button>
                  </div>
                </div>

                {/* --- Grid View (Groups by Subject) --- */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {Object.entries(groupedManuals).map(([subject, subItems]) => (
                    <Card
                      key={subject}
                      onClick={() => {
                        const firstManual = subItems.sort((a, b) => (a.order_index || 0) - (b.order_index || 0))[0];
                        if (firstManual) fetchManualDetail(firstManual.id);
                      }}
                      className="group cursor-pointer overflow-hidden border-none shadow-md ring-1 ring-slate-200 hover:ring-blue-900 transition-all rounded-[32px] md:rounded-[40px] bg-white flex flex-col h-full active:scale-[0.98]"
                    >
                      <div className="p-6 md:p-8 pb-4 bg-slate-50/30">
                        <div className="flex justify-between items-start mb-4 md:mb-6">
                          <div className="bg-blue-900 w-12 h-12 md:w-16 md:h-16 rounded-[18px] md:rounded-[24px] flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform">
                            <BookOpen size={28} />
                          </div>
                          {user?.role?.toLowerCase() === "admin" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRenameSubject(subject);
                              }}
                              className="opacity-0 group-hover:opacity-100 transition-all duration-300 text-blue-900 hover:bg-blue-100 font-black text-[11px] rounded-full border border-blue-200 bg-white shadow-sm"
                            >
                              <Tag size={14} className="ml-2"/> ערוך שם
                            </Button>
                          )}
                        </div>
                        <h3 className="text-xl md:text-2xl font-black text-slate-900 mb-3 leading-tight tracking-tight">
                          {subject}
                        </h3>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold text-blue-800 bg-blue-100/50 px-3 py-1 rounded-lg border border-blue-100">
                            {subItems[0]?.category || "כללי"}
                          </span>
                          <span className="text-[10px] font-medium text-slate-400">
                            לחץ לצפייה ב-{subItems.length} מדריכים
                          </span>
                        </div>
                      </div>
                      <div className="flex-1 px-3 md:px-4 pb-6 mt-2 space-y-1 overflow-y-auto max-h-[350px] custom-scrollbar">
                        {subItems
                          .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
                          .map((m) => (
                            <div key={m.id} className="group/container">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  fetchManualDetail(m.id);
                                }}
                                className="w-full text-right p-4 rounded-2xl hover:bg-blue-900 hover:text-white text-slate-700 font-bold text-sm transition-all flex items-center justify-between group/item border border-transparent"
                              >
                                <span className="truncate block">{m.title}</span>
                                <ChevronLeft size={18} className="shrink-0 opacity-40 group-hover/item:opacity-100 transition-all ml-2" />
                              </button>
                            </div>
                          ))}
                      </div>
                      <div className="h-1.5 w-full bg-slate-100 group-hover:bg-blue-900 transition-colors mt-auto"></div>
                    </Card>
                  ))}
                </div>
                {!isManualLoading && manuals.length === 0 && (
                  <div className="text-center py-16 animate-in fade-in duration-500">
                    <div className="text-5xl mb-4">🔍</div>
                    <h3 className="text-2xl font-black text-slate-700">לא מצאנו מדריכים מתאימים</h3>
                    <p className="text-slate-500 font-medium mt-2">נסו לחפש במילים אחרות או לשנות את הקטגוריה.</p>
                  </div>
                )}
                {/* Pagination Controls */}
{totalManuals > LIMIT && (
  <div className="flex justify-center items-center gap-4 mt-12 pb-12">
    <Button
      variant="outline"
      disabled={page === 0}
      onClick={() => handleManualSearch(page - 1)}
      className="rounded-full font-black px-6 shadow-sm"
    >
      <ChevronRight size={16} className="ml-2"/> הקודם
    </Button>

    <span className="font-black text-blue-900 bg-blue-50 px-4 py-2 rounded-xl text-sm">
      עמוד {page + 1} מתוך {Math.ceil(totalManuals / LIMIT)}
    </span>

    <Button
      variant="outline"
      disabled={(page + 1) * LIMIT >= totalManuals}
      onClick={() => handleManualSearch(page + 1)}
      className="rounded-full font-black px-6 shadow-sm"
    >
      הבא <ChevronLeft size={16} className="mr-2"/>
    </Button>
  </div>
)}
              </>
            ) : (
              /* --- Detail View --- */
              <div className="max-w-5xl mx-auto animate-in zoom-in-95 duration-500">
                <Button
                  variant="ghost"
                  onClick={() => { setSelectedManual(null); setManualSequence(null); window.history.pushState(null, '', '/'); }}
                  className="mb-8 font-black text-slate-400 gap-2 hover:text-blue-900 hover:bg-blue-50 rounded-full transition-all group"
                >
                  <ChevronLeft size={18} className="group-hover:-translate-x-1 transition-transform" /> חזרה למרכז המדריכים
                </Button>

                {/* Stepper */}
                {manualSequence?.sequence && manualSequence.sequence.length > 1 && (
                  <div className="mb-6 flex flex-nowrap items-center justify-start md:justify-center gap-2 overflow-x-auto pb-4 px-2 no-scrollbar animate-in fade-in slide-in-from-top-4 duration-700">
                    {manualSequence.sequence.map((step, idx) => (
                      <React.Fragment key={step.id}>
                        <div
                          onClick={() => fetchManualDetail(step.id)}
                          className={`cursor-pointer shrink-0 px-3 py-1.5 rounded-full text-[10px] md:text-[11px] font-black transition-all border-2 ${
                            step.is_current ? "bg-blue-900 text-white border-blue-900 shadow-md" : "bg-white text-slate-400 border-slate-100 hover:border-blue-200"
                          }`}
                        >
                          {idx + 1}. {step.title}
                        </div>
                        {idx < manualSequence.sequence.length - 1 && <ChevronLeft size={12} className="text-slate-300 shrink-0" />}
                      </React.Fragment>
                    ))}
                  </div>
                )}

                <Card className="p-0 border-none shadow-2xl rounded-[32px] md:rounded-[48px] bg-white ring-1 ring-slate-100 overflow-hidden">
                  <div className="px-5 md:px-10 py-8 md:py-12 bg-slate-50/50 border-b border-slate-100">
                    <div className="flex flex-col lg:flex-row justify-between items-start gap-6 md:gap-8">
                      <div className="space-y-3 md:space-y-4 w-full">
                        <div className="flex gap-2">
                          <span className="bg-blue-900 text-white text-[9px] md:text-[10px] font-black px-3 py-1 md:px-4 md:py-1.5 rounded-full uppercase tracking-widest shadow-sm">
                            {selectedManual.category}
                          </span>
                        </div>
                        <h2 className="text-2xl md:text-5xl font-black text-slate-900 leading-tight tracking-tight break-words">
                          {selectedManual.title}
                        </h2>
                        <div className="flex flex-wrap gap-1.5">
                          {selectedManual.tags?.map((tag, i) => (
                            <span key={i} className="bg-blue-50 text-blue-700 text-[9px] md:text-[10px] font-bold px-2.5 py-1 rounded-full border border-blue-100 flex items-center gap-1.5">
                              #{tag}
                              {user?.role?.toLowerCase() === "admin" && (
                                <button onClick={() => handleRemoveTag(selectedManual.id, tag)} className="hover:text-red-500 p-0.5">
                                  <X size={10}/>
                                </button>
                              )}
                            </span>
                          ))}
                          {user?.role?.toLowerCase() === "admin" && (
                            <Button variant="ghost" size="sm" onClick={() => handleAddTag(selectedManual.id, selectedManual.tags || [])} className="rounded-full h-7 text-[10px] font-black text-blue-600 hover:bg-blue-100/50">
                              + הוסף תגית
                            </Button>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                        <Button
                          onClick={() => handleDownload(selectedManual.id, selectedManual.filename || "")}
                          className="h-12 md:h-14 px-6 md:px-8 rounded-2xl bg-blue-900 text-white font-black shadow-lg hover:scale-[1.02] transition-all gap-3 active:scale-95 w-full sm:flex-1 lg:w-auto"
                        >
                          <Download size={18} /> מסמך מקור
                        </Button>
                        {user?.role?.toLowerCase() === "admin" && (
                          <div className="flex gap-2 justify-between sm:justify-start">
                             <Button variant="outline" onClick={openFullEditModal} className="h-12 w-full sm:w-12 md:h-14 md:w-14 rounded-2xl border-slate-200 hover:bg-slate-50 flex-1 sm:flex-none"><Pencil size={18}/></Button>
                             <Button variant="outline" onClick={() => handleReanalyze(selectedManual.id)} className="h-12 w-full sm:w-12 md:h-14 md:w-14 rounded-2xl border-slate-200 hover:bg-slate-50 flex-1 sm:flex-none"><Zap size={18}/></Button>
                             <Button variant="outline" onClick={() => handleDeleteManual(selectedManual.id, selectedManual.title)} className="h-12 w-full sm:w-12 md:h-14 md:w-14 rounded-2xl border-red-100 text-red-500 hover:bg-red-50 flex-1 sm:flex-none"><Trash2 size={20}/></Button>
                          </div>
                        )}
                      </div>
                    </div>

                    {selectedManual.summary && (
                      <div className="mt-8 md:mt-10 p-5 md:p-6 bg-blue-900 rounded-[24px] text-white shadow-xl flex flex-col md:flex-row gap-4 items-start md:items-center animate-in slide-in-from-right duration-700">
                        <div className="bg-white/20 p-2.5 rounded-xl shadow-inner shrink-0">
                          <Lightbulb size={20} />
                        </div>
                        <p className="font-bold text-base md:text-lg leading-relaxed text-right">
                          {selectedManual.summary}
                        </p>
                      </div>
                    )}

                    {selectedManual.videoUrl && (
                      <div className="mt-8 md:mt-10 rounded-[24px] md:rounded-[32px] overflow-hidden shadow-2xl aspect-video bg-black relative group">
                        {user?.role?.toLowerCase() === "admin" && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteVideo(selectedManual.id)}
                            className="absolute top-3 left-3 z-10 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity rounded-full px-4"
                          >
                            הסר סרטון
                          </Button>
                        )}
                        <iframe className="w-full h-full" src={selectedManual.videoUrl.replace("watch?v=", "embed/")} allowFullScreen></iframe>
                      </div>
                    )}
                  </div>

                  {/* Steps */}
                  <div className="px-4 md:px-10 py-10 md:py-16 space-y-12 md:space-y-20">
  {selectedManual.steps?.map((step, idx) => (
    <div
      key={idx}
      id={`step-${idx + 1}`}
      className={`relative flex flex-col md:flex-row items-start p-6 md:p-8 rounded-[32px] md:rounded-[40px] transition-all duration-700 ease-out ${
        highlightedStep === idx + 1 ? "bg-blue-50 ring-2 ring-blue-500 shadow-xl scale-[1.02]" : "hover:bg-slate-50/50"
      }`}
    >
      <div className="h-10 w-10 md:h-12 md:w-12 rounded-2xl bg-blue-900 text-white flex items-center justify-center font-black text-lg md:text-xl shadow-lg shrink-0 mb-4 md:mb-0">
        {idx + 1}
      </div>

      <div className="flex-1 space-y-4 text-right pr-0 md:pr-16 w-full">
        <h4 className="font-black text-xl md:text-2xl text-slate-900 tracking-tight leading-tight">
          {step.title}
        </h4>
        <p className="text-slate-500 font-medium text-lg md:text-xl leading-relaxed max-w-4xl break-words">
          {step.description}
        </p>

        {/* --- תיבת דגש וטיפ מאוחדת ויוקרתית --- */}
        {(step.example || step.visual_note) && (
          <div className="p-8 bg-emerald-50/50 rounded-[40px] border border-emerald-100 flex gap-6 items-start shadow-sm">
            <div className="bg-emerald-100 p-3 rounded-2xl text-emerald-600 shrink-0">
              <Lightbulb size={24} />
            </div>
            <div className="min-w-0 flex-1">
              {step.example && (
                <>
                  <span className="text-[11px] font-black text-emerald-600 uppercase block mb-1 tracking-widest">דגש לביצוע</span>
                  <p className="text-slate-700 font-bold text-xl leading-relaxed italic">{step.example}</p>
                </>
              )}

              {/* הטיפ החזותי מוטמע כאן כהערה קטנה מתחת לדגש */}
              {step.visual_note && (
                <div className={`flex items-center gap-2 text-slate-400 ${step.example ? "mt-4 pt-4 border-t border-emerald-100" : ""}`}>
                  <ImageIcon size={14} className="shrink-0 text-blue-400/60" />
                  <span className="text-xs font-bold italic">
                    <span className="font-black text-blue-500/70 ml-1 uppercase">טיפ חזותי:</span> {step.visual_note}
                  </span>
                </div>
              )}
            </div>
          </div> /* סגירת הדיב של התיבה הירוקה (כאן הייתה החריגה) */
        )}
      </div>
    </div>
  ))}
</div>

                  {(manualSequence?.next_step || manualSequence?.prev_step) && (
                    <div className="p-12 bg-blue-50/50 border-t border-blue-100 flex items-center justify-between">
                      {manualSequence.prev_step ? (
                        <Button variant="outline" onClick={() => fetchManualDetail(manualSequence.prev_step!.id)} className="h-14 px-6 rounded-2xl border-blue-200 text-blue-900 font-black">
                          <ChevronRight size={20} className="ml-2" /> {manualSequence.prev_step.title}
                        </Button>
                      ) : <div />}
                      {manualSequence.next_step ? (
                        <Button onClick={() => fetchManualDetail(manualSequence.next_step!.id)} className="h-14 px-8 rounded-2xl bg-blue-900 text-white font-black shadow-xl">
                          {manualSequence.next_step.title} <ChevronLeft size={20} className="mr-2" />
                        </Button>
                      ) : <div />}
                    </div>
                  )}
                </Card>
              </div>
            )}
          </div>
        )}

        {/* ====== TENDERS TAB ====== */}
        {activeTab === "tenders" && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            {!selectedTender ? (
              <>
                <div className="text-center mb-12">
                  <h2 className="text-4xl font-black text-slate-900 mb-2 tracking-tight">מרכז המכרזים</h2>
                  <p className="text-slate-500 font-bold">ניהול וניתוח מכרזים — מה מגיע לי?</p>
                </div>
                {/* Search Bar */}
                <div className="mb-8 relative max-w-2xl mx-auto">
                  <div className="relative group">
                    <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-900 transition-colors z-10 w-6 h-6" />
                    <Input
                      placeholder="חיפוש במכרזים (שם מכרז, ספק, מילות מפתח)..."
                      value={tenderSearch}
                      onChange={(e) => setTenderSearch(e.target.value)}
                      className="w-full h-16 pl-4 pr-14 rounded-full border-none bg-white shadow-lg ring-1 ring-slate-100 focus-visible:ring-2 focus-visible:ring-blue-900 text-lg font-bold"
                    />
                  </div>
                </div>

                {/* Tenders Grid */}
                {isTenderLoading ? (
                  <div className="flex justify-center py-16">
                    <Loader2 className="animate-spin text-blue-900" size={40} />
                  </div>
                ) : tenders.length === 0 ? (
                  <div className="text-center py-16 animate-in fade-in duration-500">
                    <FileText size={48} className="mx-auto mb-4 text-slate-300" />
                    <h3 className="text-2xl font-black text-slate-700">אין מכרזים במערכת</h3>
                    <p className="text-slate-500 font-medium mt-2">העלה מסמך מכרז או הדבק טקסט לניתוח</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {tenders
                      .filter(t => !tenderSearch || t.tender_name?.includes(tenderSearch) || t.summary?.includes(tenderSearch))
                      .map((tender) => (
                      <Card
                        key={tender.id}
                        onClick={() => fetchTenderDetail(tender.id)}
                        className="group cursor-pointer overflow-hidden border-none shadow-md ring-1 ring-slate-200 hover:ring-blue-900 transition-all rounded-[32px] bg-white flex flex-col h-full active:scale-[0.98]"
                      >
                        <div className="p-6 md:p-8 bg-slate-50/30 flex-1">
                          <div className="flex justify-between items-start mb-4">
                            <div className="bg-amber-500 w-12 h-12 rounded-[18px] flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform">
                              <FileText size={24} />
                            </div>
                            {tender.submission_deadline && tender.submission_deadline !== "לא צוין במסמך" && (
                              <span className="text-[10px] font-black text-red-600 bg-red-50 px-3 py-1 rounded-lg border border-red-100">
                                📅 {tender.submission_deadline}
                              </span>
                            )}
                          </div>
                          <h3 className="text-lg font-black text-slate-900 mb-2 leading-tight tracking-tight">
                            {tender.tender_name}
                          </h3>
                          {tender.publisher && tender.publisher !== "לא צוין במסמך" && (
                            <div className="flex items-center gap-1.5 text-xs text-slate-400 font-bold mb-3">
                              <Building2 size={12} />
                              {tender.publisher}
                            </div>
                          )}
                          <p className="text-sm text-slate-500 font-medium leading-relaxed line-clamp-3">
                            {tender.summary || "טרם נותח"}
                          </p>
                        </div>
                        <div className="px-6 pb-4 flex items-center gap-2">
                          {Array.isArray(tender.entitlements) && tender.entitlements.length > 0 && (
                            <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-100">
                              🎯 {tender.entitlements.length} זכאויות
                            </span>
                          )}
                          {tender.tender_number && tender.tender_number !== "לא צוין במסמך" && (
                            <span className="text-[10px] font-bold text-blue-700 bg-blue-50 px-2.5 py-1 rounded-lg border border-blue-100">
                              #{tender.tender_number}
                            </span>
                          )}
                        </div>
                        <div className="h-1.5 w-full bg-slate-100 group-hover:bg-amber-500 transition-colors mt-auto"></div>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            ) : (
              /* --- Tender Detail View --- */
              <div className="max-w-5xl mx-auto animate-in zoom-in-95 duration-500">
                <Button
                  variant="ghost"
                  onClick={() => setSelectedTender(null)}
                  className="mb-8 font-black text-slate-400 gap-2 hover:text-blue-900 hover:bg-blue-50 rounded-full transition-all group"
                >
                  <ChevronLeft size={18} className="group-hover:-translate-x-1 transition-transform" /> חזרה לרשימת המכרזים
                </Button>

                <Card className="p-0 border-none shadow-2xl rounded-[32px] md:rounded-[48px] bg-white ring-1 ring-slate-100 overflow-hidden">
                  {/* Header */}
                  <div className="px-5 md:px-10 py-8 md:py-12 bg-gradient-to-bl from-amber-50 via-white to-white border-b border-slate-100">
                    <div className="flex flex-col lg:flex-row justify-between items-start gap-6">
                      <div className="space-y-3 w-full">
                        <div className="flex gap-2 flex-wrap">
                          <span className="bg-amber-500 text-white text-[10px] font-black px-4 py-1.5 rounded-full uppercase tracking-widest shadow-sm">
                            מכרז
                          </span>
                          {selectedTender.tender_number && selectedTender.tender_number !== "לא צוין במסמך" && (
                            <span className="bg-blue-50 text-blue-700 text-[10px] font-black px-3 py-1.5 rounded-full border border-blue-100">
                              #{selectedTender.tender_number}
                            </span>
                          )}
                        </div>
                        <h2 className="text-2xl md:text-4xl font-black text-slate-900 leading-tight tracking-tight">
                          {selectedTender.tender_name}
                        </h2>
                        {selectedTender.publisher && selectedTender.publisher !== "לא צוין במסמך" && (
                          <div className="flex items-center gap-2 text-sm text-slate-500 font-bold">
                            <Building2 size={16} />
                            {selectedTender.publisher}
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto shrink-0">
                        {selectedTender.source_filename && (
                          <Button
                            onClick={() => handleDownloadTender(selectedTender.id, selectedTender.source_filename || "")}
                            className="h-12 md:h-14 px-6 md:px-8 rounded-2xl bg-blue-900 text-white font-black shadow-lg hover:scale-[1.02] transition-all gap-3 active:scale-95"
                          >
                            <Download size={18} /> מסמך מקור
                          </Button>
                        )}
                        {user?.role?.toLowerCase() === "admin" && (
                          <Button variant="outline" onClick={() => handleDeleteTender(selectedTender.id, selectedTender.tender_name)} className="h-12 md:h-14 px-6 rounded-2xl border-red-100 text-red-500 hover:bg-red-50">
                            <Trash2 size={18} className="ml-2" /> מחק
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Summary */}
                    {selectedTender.summary && (
                      <div className="mt-8 p-5 md:p-6 bg-amber-500 rounded-[24px] text-white shadow-xl flex flex-col md:flex-row gap-4 items-start md:items-center animate-in slide-in-from-right duration-700">
                        <div className="bg-white/20 p-2.5 rounded-xl shadow-inner shrink-0">
                          <Lightbulb size={20} />
                        </div>
                        <p className="font-bold text-base md:text-lg leading-relaxed text-right">
                          {selectedTender.summary}
                        </p>
                      </div>
                    )}

                    {/* Dates Metadata Row */}
                    <div className="mt-6 flex flex-wrap gap-3">
                      {selectedTender.publication_date && selectedTender.publication_date !== "לא צוין במסמך" && (
                        <div className="flex items-center gap-2 bg-blue-50 px-4 py-2.5 rounded-2xl border border-blue-100">
                          <Calendar size={14} className="text-blue-500" />
                          <div className="text-xs">
                            <span className="font-black text-blue-900 block">תאריך פרסום</span>
                            <span className="text-blue-600 font-bold">{selectedTender.publication_date}</span>
                          </div>
                        </div>
                      )}
                      {selectedTender.submission_deadline && selectedTender.submission_deadline !== "לא צוין במסמך" && (
                        <div className="flex items-center gap-2 bg-red-50 px-4 py-2.5 rounded-2xl border border-red-100">
                          <Calendar size={14} className="text-red-500" />
                          <div className="text-xs">
                            <span className="font-black text-red-900 block">תאריך הגשה אחרון</span>
                            <span className="text-red-600 font-bold">{selectedTender.submission_deadline}</span>
                          </div>
                        </div>
                      )}
                      {selectedTender.validity_period && selectedTender.validity_period !== "לא צוין במסמך" && (
                        <div className="flex items-center gap-2 bg-emerald-50 px-4 py-2.5 rounded-2xl border border-emerald-100">
                          <Calendar size={14} className="text-emerald-500" />
                          <div className="text-xs">
                            <span className="font-black text-emerald-900 block">תוקף המכרז</span>
                            <span className="text-emerald-600 font-bold">{selectedTender.validity_period}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Entitlements - "מה מגיע לי" */}
                  <div className="px-5 md:px-10 py-10 md:py-14">
                    <div className="flex items-center gap-3 mb-8">
                      <div className="bg-emerald-100 p-2.5 rounded-xl text-emerald-600">
                        <Lightbulb size={20} />
                      </div>
                      <h3 className="text-2xl font-black text-slate-900 tracking-tight">🎯 מה מגיע לי מתוך המכרז</h3>
                    </div>
                    <div className="space-y-4">
                      {(Array.isArray(selectedTender.entitlements) ? selectedTender.entitlements : []).map((item, idx) => (
                        <div key={idx} className="flex items-start gap-4 p-5 bg-emerald-50/50 rounded-[20px] border border-emerald-100 animate-in slide-in-from-right duration-500" style={{ animationDelay: `${idx * 80}ms` }}>
                          <div className="bg-emerald-500 text-white w-8 h-8 rounded-xl flex items-center justify-center font-black text-sm shrink-0 shadow-sm">
                            {idx + 1}
                          </div>
                          <p className="text-slate-700 font-bold text-base leading-relaxed">{item}</p>
                        </div>
                      ))}
                      {(!Array.isArray(selectedTender.entitlements) || selectedTender.entitlements.length === 0) && (
                        <p className="text-slate-400 font-medium text-center py-8">לא נמצאו פרטי זכאות במכרז</p>
                      )}
                    </div>

                    {/* Key Requirements */}
                    {selectedTender.structured_data?.key_requirements?.length > 0 && (
                      <div className="mt-12">
                        <h3 className="text-xl font-black text-slate-900 mb-6 tracking-tight">📋 דרישות מרכזיות</h3>
                        <div className="space-y-3">
                          {selectedTender.structured_data.key_requirements.map((req: string, idx: number) => (
                            <div key={idx} className="flex items-start gap-3 p-4 bg-slate-50 rounded-[16px] border border-slate-100">
                              <span className="w-2 h-2 rounded-full bg-blue-900 mt-2 shrink-0"></span>
                              <p className="text-slate-600 font-medium text-sm leading-relaxed">{req}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Appendices */}
                    {selectedTender.appendices && selectedTender.appendices.length > 0 && (
                      <div className="mt-12">
                        <h3 className="text-xl font-black text-slate-900 mb-6 tracking-tight">📎 נספחים למכרז</h3>
                        <div className="space-y-3">
                          {selectedTender.appendices.map((appx, idx) => (
                            <div key={idx} className="flex items-center gap-3 p-4 bg-slate-50 rounded-[16px] border border-slate-100 hover:bg-emerald-50 transition-colors">
                              <div className="bg-emerald-100 text-emerald-600 p-2.5 rounded-xl">
                                <Link size={18} />
                              </div>
                              <span className="text-slate-700 font-bold text-sm">{appx.filename}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              </div>
            )}
          </div>
        )}

        {activeTab === "admin" && <AdminDashboard />}

        {/* Uploads Overlay */}
        {activeTab === "uploads" && (
          <div
            className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/80 backdrop-blur-md p-2 md:p-4 animate-in fade-in duration-300"
            onClick={() => setActiveTab("training")}
          >
            <div
              className="max-w-2xl w-full mx-auto max-h-[95vh] flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              <Card className="p-5 md:p-10 border-none shadow-[0_32px_64px_rgba(0,0,0,0.4)] rounded-[32px] md:rounded-[40px] bg-white relative overflow-hidden flex flex-col">
                <Button
                  variant="secondary"
                  size="icon"
                  onClick={() => setActiveTab("training")}
                  className="absolute right-4 top-4 h-10 w-10 md:h-12 md:w-12 rounded-full bg-slate-100 hover:bg-red-50 text-slate-500 hover:text-red-600 transition-all z-50"
                >
                  <X className="w-6 h-6 md:w-8 md:h-8" />
                </Button>
                <div className="overflow-y-auto pr-1 pl-1 custom-scrollbar mt-6 md:mt-0">
                  <div className="flex items-center gap-4 mb-6 md:mb-8">
                    <div className="bg-blue-100 p-3 md:p-4 rounded-2xl text-blue-900 shrink-0">
                      {uploadType === "file" ? <UploadCloud className="w-7 h-7 md:w-8 md:h-8" /> : <Play className="w-7 h-7 md:w-8 md:h-8" />}
                    </div>
                    <div>
                      <h2 className="text-xl md:text-2xl font-black text-slate-900 leading-tight">ניהול תוכן {isTender ? "מכרזים" : "הדרכה"}</h2>
                      <p className="text-slate-400 font-bold text-xs md:text-sm">העלאה ועדכון {isTender ? "מכרזים וחוזים" : "מדריכים לוגיסטיים"}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 bg-slate-100 p-1.5 rounded-2xl mb-6 shadow-inner">
                    <button
                      onClick={() => setUploadType("file")}
                      className={`flex-1 py-3 rounded-xl font-black text-xs md:text-sm transition-all ${
                        uploadType === "file" ? "bg-blue-900 text-white shadow-md" : "text-slate-500 hover:bg-white"
                      }`}
                    >
                      📄 מסמך
                    </button>
                    <button
                      onClick={() => setUploadType("video")}
                      className={`flex-1 py-3 rounded-xl font-black text-xs md:text-sm transition-all ${
                        uploadType === "video" ? "bg-blue-900 text-white shadow-md" : "text-slate-500 hover:bg-white"
                      }`}
                    >
                      🎬 סרטון
                    </button>
                  </div>

                  {uploadType === "file" ? (
                    <div className="space-y-5 animate-in fade-in duration-500 pb-4">
                      <div className="space-y-2">
                        <Label className="font-black text-slate-700 text-sm mr-2">סוג המסמך</Label>
                        <div className="flex gap-2 p-1.5 bg-slate-100 rounded-2xl border border-slate-200">
                          <button
                            onClick={() => setIsTender(false)}
                            className={`flex-1 py-2.5 rounded-xl font-black text-xs transition-all ${
                              !isTender ? "bg-white text-blue-900 shadow-sm ring-1 ring-slate-200" : "text-slate-500 hover:text-slate-700"
                            }`}
                          >
                            📖 מדריך לוגיסטי
                          </button>
                          <button
                            onClick={() => setIsTender(true)}
                            className={`flex-1 py-2.5 rounded-xl font-black text-xs transition-all ${
                              isTender ? "bg-white text-blue-900 shadow-sm ring-1 ring-slate-200" : "text-slate-500 hover:text-slate-700"
                            }`}
                          >
                            ⚖️ מכרז / חוזה
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="font-black text-slate-700 text-sm mr-2">
                          {isTender ? "1. תחום המכרז" : "1. מודול SAP"}
                        </Label>
                        <Select value={uploadModule} onValueChange={setUploadModule}>
                          <SelectTrigger className={`h-14 rounded-xl bg-slate-50 font-bold border-none ring-1 transition-all ${
                            !uploadModule ? "ring-red-200" : "ring-slate-200 focus:ring-blue-900"
                          }`}>
                            <SelectValue placeholder={isTender ? "בחר תחום..." : "בחר מודול..."} />
                          </SelectTrigger>
                          <SelectContent className="z-[300] rounded-xl font-bold shadow-2xl border-slate-100">
                            {isTender ? (
                              <>
                                <SelectItem value="construction">🏗️ בינוי ותשתיות</SelectItem>
                                <SelectItem value="technology">💻 טכנולוגיות ותקשוב</SelectItem>
                                <SelectItem value="logistics">📦 רכש לוגיסטי</SelectItem>
                                <SelectItem value="transport">🚗 רכב ותחבורה</SelectItem>
                                <SelectItem value="cleaning_catering">🧹 שירותי נקיון והסעדה</SelectItem>
                                <SelectItem value="security">🛡️ אבטחה ומיגון</SelectItem>
                                <SelectItem value="professional_services">🤝 שירותים מקצועיים</SelectItem>
                              </>
                            ) : (
                              <>
                                <SelectItem value="inventory">📦 ניהול מלאי</SelectItem>
                                <SelectItem value="fleet">🚗 צי רכב</SelectItem>
                                <SelectItem value="procurement">🛒 רכש ואספקה</SelectItem>
                                <SelectItem value="general">📂 כללי / מנהלה</SelectItem>
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="font-black text-slate-700 text-sm mr-2">סוג פעולה</Label>
                        <div className="flex gap-2 p-1.5 bg-slate-100 rounded-2xl border border-slate-200">
                          <button
                            onClick={() => setUploadActionType("new")}
                            className={`flex-1 py-2.5 rounded-xl font-black text-xs transition-all ${
                              uploadActionType === "new" ? "bg-blue-900 text-white shadow-sm" : "text-slate-500 hover:text-slate-700 hover:bg-slate-200"
                            }`}
                          >
                            ✨ מסמך חדש
                          </button>
                          <button
                            onClick={() => setUploadActionType("update")}
                            className={`flex-1 py-2.5 rounded-xl font-black text-xs transition-all ${
                              uploadActionType === "update" ? "bg-blue-900 text-white shadow-sm" : "text-slate-500 hover:text-slate-700 hover:bg-slate-200"
                            }`}
                          >
                            🔄 עדכון קיים
                          </button>
                          <button
                            onClick={() => setUploadActionType("appendix")}
                            className={`flex-1 py-2.5 rounded-xl font-black text-xs transition-all ${
                              uploadActionType === "appendix" ? "bg-blue-900 text-white shadow-sm" : "text-slate-500 hover:text-slate-700 hover:bg-slate-200"
                            }`}
                          >
                            📎 הוספת נספח
                          </button>
                        </div>
                      </div>

                      {uploadActionType !== "new" && (
                        <div className="space-y-2">
                          <Label className="font-black text-slate-700 text-sm mr-2">בחר מסמך יעד (שיוך נספח/עדכון)</Label>
                          <Select value={uploadTargetId} onValueChange={setUploadTargetId}>
                            <SelectTrigger className={`h-14 rounded-xl bg-slate-50 font-bold border-none ring-1 transition-all ${
                              !uploadTargetId ? "ring-red-200" : "ring-slate-200 focus:ring-blue-900"
                            }`}>
                              <SelectValue placeholder="חיפוש / בחירת מסמך קיים..." />
                            </SelectTrigger>
                            <SelectContent className="z-[300] rounded-xl font-bold shadow-2xl border-slate-100 max-h-[250px]">
                              {isTender ? (
                                tenders.map(t => (
                                  <SelectItem key={t.id} value={t.id.toString()}>{t.tender_name}</SelectItem>
                                ))
                              ) : (
                                allManuals.map(m => (
                                  <SelectItem key={m.id} value={m.id.toString()}>{m.title}</SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                          
                          <div className="flex items-center gap-2 mt-4 p-3 bg-amber-50 rounded-xl border border-amber-100">
                            <input 
                              type="checkbox" 
                              id="reanalyze-doc" 
                              checked={reanalyzeDocument} 
                              onChange={(e) => setReanalyzeDocument(e.target.checked)}
                              className="w-5 h-5 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                            />
                            <Label htmlFor="reanalyze-doc" className="font-bold text-amber-900 cursor-pointer text-sm">
                              האם לנתח מחדש את המסמך באמצעות AI? (יעדכן את התקציר)
                            </Label>
                          </div>
                        </div>
                      )}

                      {uploadActionType === "new" && (
                        <div className="bg-blue-50/50 p-4 md:p-6 rounded-[24px] border border-blue-100 space-y-3">
                          <Label className="font-black text-blue-900 block text-base">
                             {isTender ? "2. שם המכרז / ספק" : "2. שם הרובריקה (הנושא)"}
                          </Label>
                          <Input
                            value={uploadSubCategory}
                            onChange={(e) => setUploadSubCategory(e.target.value)}
                            placeholder={isTender ? "למשל: מכרז אספקת ציוד משרדי" : "למשל: ספירת מלאי"}
                            className="h-14 rounded-xl border-none bg-white ring-1 ring-slate-200 font-bold text-lg focus:ring-2 focus:ring-blue-900 shadow-sm"
                          />
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label className="font-black text-slate-700 text-sm mr-2">3. בחר קובץ מקור</Label>
                        <div className={`border-2 border-dashed rounded-[24px] p-6 text-center transition-all ${selectedFile ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
                          <input type="file" id="fileUpload" className="hidden" accept=".pdf,.doc,.docx,.pptx,.xls,.xlsx,.csv" onChange={e => setSelectedFile(e.target.files?.[0] || null)} />
                          <label htmlFor="fileUpload" className="cursor-pointer block space-y-2">
                            <div className={`mx-auto h-12 w-12 rounded-full flex items-center justify-center ${selectedFile ? "bg-emerald-100 text-emerald-600" : "bg-blue-50 text-blue-400"}`}>
                              <UploadCloud className="w-6 h-6" />
                            </div>
                            <p className="text-sm font-black text-slate-900 truncate px-2">
                              {selectedFile ? selectedFile.name : "לחץ לבחירת קובץ"}
                            </p>
                          </label>
                        </div>
                      </div>
                      <Button
                         onClick={handleUploadAction}
                            disabled={
                                isUploading ||
                                !selectedFile ||
                                !uploadModule ||
                            (uploadActionType === "new" ? !uploadSubCategory : !uploadTargetId)
                            }
                            className="w-full h-14 md:h-16 rounded-xl bg-blue-900 text-white font-black text-lg shadow-xl active:scale-95 disabled:bg-slate-200 disabled:text-slate-400 transition-all mt-4"
                            >
                         {isUploading ? <Loader2 className="animate-spin mx-auto" /> : `אשר העלאה וניתוח ${isTender ? "מכרז" : "AI"}`}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-6 animate-in fade-in duration-500 pb-4">
                      <div className="space-y-2">
                        <Label className="font-black text-slate-700 text-sm mr-2">בחר מדריך קיים</Label>
                        <Select value={selectedManualForVideo} onValueChange={setSelectedManualForVideo}>
                          <SelectTrigger className="rounded-xl h-14 bg-slate-50 font-bold border-none ring-1 ring-slate-200"><SelectValue placeholder="חפש מדריך..." /></SelectTrigger>
                          <SelectContent className="z-[300]">{allManuals.map(m => <SelectItem key={m.id} value={m.id.toString()}>{m.title}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="font-black text-slate-700 text-sm mr-2">קישור YouTube</Label>
                        <Input value={videoUrlInput} onChange={e => setVideoUrlInput(e.target.value)} placeholder="הדבק קישור..." className="h-14 rounded-xl border-none bg-slate-50 ring-1 ring-slate-200 font-medium" />
                      </div>
                      <Button
                        onClick={handleVideoUpdate}
                        disabled={!selectedManualForVideo || !videoUrlInput || isUploading}
                        className="w-full h-14 md:h-16 rounded-xl bg-blue-900 text-white font-black text-lg shadow-lg active:scale-95 transition-all"
                      >
                        שייך סרטון
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </div>
        )}
      </main>

      {/* Floating Chat */}
      <div className="fixed inset-x-0 bottom-0 z-[999] p-4 pointer-events-none flex flex-col items-center">
        {!isChatMinimized && (
          <div className="mx-auto w-full max-w-[95vw] md:max-w-4xl pointer-events-auto animate-in slide-in-from-bottom-12 duration-500">
            <div className="mb-4 overflow-hidden rounded-[32px] border-2 border-slate-200 bg-white shadow-[0_20px_60px_rgba(0,0,0,0.15)] flex flex-col">
              <div className="px-6 py-4 bg-slate-50 border-b flex justify-between items-center">
                <div className="flex items-center gap-3">
                   <div className="h-3 w-3 bg-emerald-500 rounded-full animate-pulse"></div>
                   <span className="text-xs font-black uppercase tracking-widest text-slate-500">פקד טוביה מחובר</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={handleClearChat} className="h-8 font-black rounded-lg hover:bg-red-100 hover:text-red-600 text-slate-500 transition-colors">
                    <Trash2 size={14} className="ml-1"/> נקה שיחה
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setIsChatMinimized(true)} className="h-8 font-black rounded-lg hover:bg-slate-200">
                    <ChevronDown size={14} className="ml-1"/>X
                  </Button>
                </div>
              </div>
              <div ref={scrollRef} className="p-6 space-y-6 overflow-y-auto bg-white custom-scrollbar" style={{ height: '350px' }}>
                {messages.map((msg, idx) => (
                    <div
    key={idx}
    // השורה הזו היא הקסם - היא מסמנת למערכת איפה השאלה האחרונה
    ref={idx === messages.length - 2 && msg.role === "user" ? lastUserMsgRef : null}
    className={`flex ${msg.role === "user" ? "justify-start" : "justify-end"}`}
  >
                        <div className={`relative max-w-[85%] px-4 py-3 shadow-sm ${
                        msg.role === "user"
                            ? "bg-[#DCF8C6] text-slate-800 rounded-l-2xl rounded-br-2xl rounded-tr-sm ml-2"
                            : "bg-white text-slate-800 rounded-r-2xl rounded-bl-2xl rounded-tl-sm mr-2 border border-slate-100"
                        }`}>
                        <div className={`absolute top-0 w-2 h-2 ${
                            msg.role === "user"
                            ? "-right-1 bg-[#DCF8C6] [clip-path:polygon(0_0,0_100%,100%_0)]"
                            : "-left-1 bg-white border-l border-t border-slate-100 [clip-path:polygon(100%_0,100%_100%,0_0)]"
                        }`}></div>
                        <div className="text-right text-sm leading-relaxed prose prose-sm max-w-none">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                        </div>
                        <div className="text-[10px] text-slate-400 mt-1 text-left font-bold uppercase">
                            {msg.role === "user" ? "נשלח" : "פקד טוביה"}
                        </div>
                        </div>
                    </div>
                ))}
                {isChatLoading && (
                  <div className="flex justify-end">
                    <div className="bg-blue-900/5 p-4 rounded-2xl animate-pulse">
                      <Loader2 size={20} className="animate-spin text-blue-900" />
                    </div>
                  </div>
                )}
              </div>
              <div className="p-4 bg-white border-t flex gap-3 items-center">
                <div className="relative flex-1">
                  <Input
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleChatSend()}
                    placeholder="שאל את פקד טוביה על התהליך..."
                    className="h-14 border-none bg-slate-50 rounded-2xl pr-6 font-medium focus:ring-2 focus:ring-blue-100 transition-all"
                  />
                </div>
                <Button onClick={handleChatSend} className="h-14 w-14 rounded-2xl bg-blue-900 shadow-lg shadow-blue-900/20 active:scale-90 transition-all">
                  <Send size={22} />
                </Button>
              </div>
            </div>
          </div>
        )}
        {isChatMinimized && (
          <Button
            onClick={() => setIsChatMinimized(false)}
            className="pointer-events-auto h-20 w-20 rounded-full bg-blue-900 shadow-[0_15px_40px_rgba(30,58,138,0.4)] hover:scale-110 active:scale-90 transition-all flex flex-col items-center justify-center border-4 border-white animate-in zoom-in duration-500"
          >
            <MessageSquareText size={32} />
            <span className="text-[9px] font-black mt-1 uppercase tracking-tighter">צ'אט</span>
          </Button>
        )}
      </div>

      {/* ---------------- מודל עריכת מדריך מלא ---------------- */}
      {isEditModalOpen && editingManualData && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4" dir="rtl">
          <Card className="w-full max-w-4xl max-h-[90vh] flex flex-col rounded-[32px] md:rounded-[40px] bg-white overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300">
            {/* Header */}
            <div className="p-6 md:p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <h2 className="text-2xl font-black text-blue-900 tracking-tight">עריכת מדריך מלא</h2>
              <Button variant="ghost" size="icon" onClick={() => setIsEditModalOpen(false)} className="rounded-full hover:bg-red-50 hover:text-red-500">
                <X size={24}/>
              </Button>
            </div>

            {/* Scrollable Form Area */}
            <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-8 custom-scrollbar bg-white">

              <div className="space-y-3">
                <Label className="font-black text-slate-700 text-lg">כותרת המדריך</Label>
                <Input
                  value={editingManualData.title}
                  onChange={e => setEditingManualData({...editingManualData, title: e.target.value})}
                  className="h-14 font-black text-xl rounded-2xl bg-slate-50 border-slate-200 focus-visible:ring-blue-900 px-4"
                />
              </div>

              <div className="space-y-3">
                <Label className="font-black text-slate-700 text-lg">תקציר המסמך</Label>
                <textarea
                  value={editingManualData.summary || ""}
                  onChange={e => setEditingManualData({...editingManualData, summary: e.target.value})}
                  className="w-full min-h-[120px] p-4 rounded-2xl border border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-900 outline-none resize-y font-medium text-slate-600"
                  placeholder="כתוב תקציר קצר על מטרת המדריך..."
                />
              </div>

              <div className="space-y-6">
                <div className="flex justify-between items-center border-b border-slate-100 pb-4">
                   <Label className="font-black text-slate-900 text-2xl">שלבי המדריך</Label>
                   <Button
                      type="button"
                      onClick={() => setEditingManualData({
                        ...editingManualData,
                        steps: [...(editingManualData.steps || []), {title: "שלב חדש", description: ""}]
                      })}
                      className="rounded-full bg-blue-50 text-blue-900 font-bold hover:bg-blue-100 border border-blue-200"
                   >
                     + הוסף שלב
                   </Button>
                </div>

                {editingManualData.steps?.map((step: any, index: number) => (
                  <div key={index} className="p-6 border border-slate-100 rounded-[24px] space-y-4 bg-slate-50 relative group transition-all hover:border-blue-200 hover:shadow-md">

                    <Button
                      variant="destructive"
                      size="sm"
                      className="absolute top-4 left-4 transition-opacity rounded-xl h-9"
                      onClick={() => {
                         if(!window.confirm("להסיר שלב זה?")) return;
                         const newSteps = [...editingManualData.steps];
                         newSteps.splice(index, 1);
                         setEditingManualData({...editingManualData, steps: newSteps});
                      }}
                    >
                      <Trash2 size={16} className="ml-1"/> הסר שלב
                    </Button>

                    <div className="flex gap-3 items-center w-[85%]">
                       <div className="bg-blue-900 text-white w-10 h-10 rounded-[14px] flex items-center justify-center font-black text-lg shrink-0 shadow-sm">
                         {index + 1}
                       </div>
                       <Input
                          value={step.title}
                          onChange={e => {
                             const newSteps = [...editingManualData.steps];
                             newSteps[index].title = e.target.value;
                             setEditingManualData({...editingManualData, steps: newSteps});
                          }}
                          className="flex-1 font-black h-12 rounded-xl text-lg border-slate-200 focus-visible:ring-blue-900"
                          placeholder="כותרת הפעולה בשלב זה"
                       />
                    </div>

                    <textarea
                      value={step.description}
                      onChange={e => {
                         const newSteps = [...editingManualData.steps];
                         newSteps[index].description = e.target.value;
                         setEditingManualData({...editingManualData, steps: newSteps});
                      }}
                      className="w-full p-4 rounded-xl border border-slate-200 bg-white outline-none font-medium text-slate-600 focus:ring-2 focus:ring-blue-900"
                      placeholder="תאר בפירוט את סדר הפעולות הנדרש..."
                      rows={3}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="p-6 border-t border-slate-100 bg-slate-50/80 flex gap-4">
               <Button variant="outline" className="flex-1 h-14 rounded-2xl font-bold" onClick={() => setIsEditModalOpen(false)}>ביטול</Button>
               <Button onClick={handleSaveFullEdit} disabled={isManualLoading} className="flex-1 h-14 rounded-2xl bg-blue-900 hover:bg-blue-800 text-white font-black text-lg shadow-lg">
                 {isManualLoading ? <Loader2 className="animate-spin" /> : "שמור שינויים למדריך"}
               </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}